/*
  Cart sync guard — keeps the cart drawer, and everything that listens to the theme's cart events, in step with the
  real cart in three situations theme.js (read only) doesn't cover.

  1. App proxy pages (/apps/…, e.g. Easy Bundle's builder at /apps/gbb/easybundle).
     Every cart call that asks for `sections` gets them rendered in the context of the current URL, and an app proxy
     URL can't render theme sections: Shopify answers 404 with `{ "<drawer id>": null }` (checked on the live store).
     CartDrawer._onCartChanged then parses null, dereferences what isn't there and throws — before it reaches its
     own trailing cart:refresh — so the drawer is left as it was: a removed line leaves an empty row, the totals
     don't move. On these pages the guard sets `sections_url` to the store root on any cart request that asks for
     sections without saying where to render them, so the theme, the apps and our own scripts all get a real drawer.

  2. Back/forward navigation. A page restored from the bfcache, or re-read from the HTTP cache (then theme.js does
     nothing at all), shows the cart as it was when the page was first loaded.

  3. Out-of-order refreshes. Each cart:change makes theme.js re-fetch the whole drawer, unordered. When two changes
     follow each other — the bundle goes, then the gift box sync removes its box — the older response can land last
     and repaint a cart that no longer exists.

  For 2 and 3: once cart activity settles, the guard reads the lines the drawer is showing — every line already
  renders its key and quantity on its quantity input (`data-line-key`, value) — and compares them with the latest
  cart: the one the events carried when they had it, else one /cart.js read. Only on a mismatch does it fetch the
  drawer and hand it to the theme through a normal cart:change, so the count, bars and drawer all update the way they
  do for any change. Nothing is added to the drawer's markup for this.

  One line legitimately has no input: the bundle gift box, which its bundle shows instead (snippets/line-item.liquid).
  So a cart line carrying `_gift_box` may be missing from the drawer without that counting as a mismatch.
*/
(function () {
  'use strict';

  if (window.__cartSyncGuard) return;
  window.__cartSyncGuard = true;

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var html = document.documentElement;

  /* theme.js swaps the drawer's line items 1250ms after a change; check only once that has happened. */
  var SETTLE_MS = 1600;
  /* After the guard repaints, the theme's own follow-up events are not a reason to check again. */
  var QUIET_AFTER_REPAINT_MS = 2500;

  function escapeRegExp(text) {
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  var onAppProxyPage = new RegExp('^' + escapeRegExp(root) + 'apps/').test(window.location.pathname);

  /* ---------------------------------------------------------------------------------------------------------------
     1. App proxy pages: render sections in the store root's context
     --------------------------------------------------------------------------------------------------------------- */

  function isCartMutation(input, init) {
    var method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();
    if (method !== 'POST') return false;
    var url = typeof input === 'string' ? input : input && (input.url || input.href) || '';
    return /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url);
  }

  function withSectionsUrl(init) {
    var body = init.body;

    if (typeof body === 'string') {
      if (/^\s*\{/.test(body)) {
        try {
          var data = JSON.parse(body);
          if (data && data.sections && !data.sections_url) {
            data.sections_url = root;
            return Object.assign({}, init, { body: JSON.stringify(data) });
          }
        } catch (error) {}
        return init;
      }
      var params = new URLSearchParams(body);
      if (params.has('sections') && !params.has('sections_url')) {
        params.set('sections_url', root);
        return Object.assign({}, init, { body: params.toString() });
      }
      return init;
    }

    if ((window.FormData && body instanceof FormData) || (window.URLSearchParams && body instanceof URLSearchParams)) {
      if (body.has('sections') && !body.has('sections_url')) body.set('sections_url', root);
    }
    return init;
  }

  if (onAppProxyPage && typeof window.fetch === 'function') {
    var nativeFetch = window.fetch;
    window.fetch = function (input, init) {
      try {
        if (init && init.body && isCartMutation(input, init)) init = withSectionsUrl(init);
      } catch (error) {}
      return init === undefined ? nativeFetch.call(window, input) : nativeFetch.call(window, input, init);
    };
  }

  /* ---------------------------------------------------------------------------------------------------------------
     2 & 3. Repaint a drawer that shows a cart other than the real one
     --------------------------------------------------------------------------------------------------------------- */

  function drawerElement() {
    return document.getElementById('cart-drawer') || document.querySelector('cart-drawer');
  }

  /* The lines the drawer is showing, from the quantity inputs it already renders. The drawer prints every line twice
     (the A/B cart UI and the default one), so read one copy: the default UI's. null = no drawer to judge. */
  function shownLines() {
    var drawer = drawerElement();
    if (!drawer) return null;
    var inputs = drawer.querySelectorAll('.cart-ui--default input[data-line-key]');
    if (!inputs.length) inputs = drawer.querySelectorAll('.cart-drawer__line-items input[data-line-key]');
    return Array.prototype.map.call(inputs, function (input) {
      return { key: input.getAttribute('data-line-key'), quantity: parseInt(input.defaultValue, 10) || 0 };
    });
  }

  /* Does the drawer show this cart? Every shown line must be a cart line with the same quantity, and every cart line
     must be shown — except a gift box line, which may be folded into its bundle. Keys can repeat (merged bundle lines
     can share one), so lines are matched one to one rather than looked up. */
  function drawerShows(cart) {
    var shown = shownLines();
    if (shown === null) return true;

    var pending = (cart.items || []).map(function (item) {
      return { key: item.key, quantity: item.quantity, optional: !!(item.properties && item.properties._gift_box) };
    });

    for (var i = 0; i < shown.length; i++) {
      var match = -1;
      for (var j = 0; j < pending.length; j++) {
        if (pending[j].key === shown[i].key && pending[j].quantity === shown[i].quantity) {
          match = j;
          break;
        }
      }
      if (match === -1) return false;
      pending.splice(match, 1);
    }

    return pending.every(function (line) {
      return line.optional;
    });
  }

  function sectionIdOf(element) {
    var section = element && element.closest('.shopify-section');
    return section ? section.id.replace('shopify-section-', '') : null;
  }

  function onCartPage() {
    var settings = window.themeVariables && window.themeVariables.settings;
    return !!settings && settings.pageType === 'cart';
  }

  function getCart() {
    return fetch(root + 'cart.js', { cache: 'no-store', credentials: 'same-origin' }).then(function (response) {
      if (!response.ok) throw new Error('cart.js ' + response.status);
      return response.json();
    });
  }

  var timer = null;
  var quietUntil = 0;
  var repainting = false;
  var checking = false;
  var burstCart = null;

  function schedule(delay) {
    if (repainting || Date.now() < quietUntil) return;
    clearTimeout(timer);
    timer = setTimeout(check, delay === undefined ? SETTLE_MS : delay);
  }

  async function repaint(cart) {
    /* /cart only re-renders through page loads; a stale one is simply reloaded. */
    if (onCartPage()) {
      window.location.reload();
      return;
    }

    var drawer = drawerElement();
    var sectionId = sectionIdOf(drawer);
    if (!sectionId) return;

    var response = await fetch(root + '?sections=' + encodeURIComponent(sectionId), {
      cache: 'no-store',
      credentials: 'same-origin'
    });
    var sections = response.ok ? await response.json() : null;

    quietUntil = Date.now() + QUIET_AFTER_REPAINT_MS;
    repainting = true;
    try {
      if (sections && sections[sectionId]) {
        cart.sections = sections;
        /* baseEvent 'variant:add' is what makes CartDrawer swap the line items at once. */
        html.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart: cart } }));
      } else {
        html.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }
    } finally {
      repainting = false;
    }
  }

  async function check() {
    if (checking) {
      schedule();
      return;
    }
    if (!drawerElement()) return;

    checking = true;
    try {
      var known = burstCart;
      burstCart = null;
      if (known && drawerShows(known)) return;

      var cart = await getCart();
      if (drawerShows(cart)) return;
      await repaint(cart);
    } catch (error) {
      /* Network trouble: leave the drawer to the theme. */
    } finally {
      checking = false;
    }
  }

  function remember(event) {
    var cart = event.detail && event.detail.cart;
    if (cart && cart.items) burstCart = cart;
    schedule();
  }

  document.addEventListener('cart:change', remember);
  document.addEventListener('cart:refresh', remember);

  window.addEventListener('pageshow', function (event) {
    if (event.persisted) {
      burstCart = null;
      schedule();
    }
  });

  var navigation = window.performance && performance.getEntriesByType ? performance.getEntriesByType('navigation')[0] : null;
  if (navigation && navigation.type === 'back_forward') schedule(0);
})();
